/**
 * FinanceTabs — sub-navigation strip for the Finance domain.
 * Mounted under the Command Center and on every workbench page.
 *
 * Use <FinanceNavStrip /> for the canonical full-domain strip — it
 * pulls counts from useFinanceCounts and keeps every workbench page
 * in sync (no per-page tab-list duplication).
 */
import { NavLink } from "react-router-dom";
import { useFinanceCounts } from "@/components/finance/useFinanceCounts";


interface TabDef {
  to: string;
  label: string;
  count?: number;
  tone?: "muted" | "warn" | "bad";
}

export function FinanceTabs({ tabs }: { tabs: TabDef[] }) {
  return (
    <nav className="flex items-center gap-0 border-b border-border/60 -mx-1 overflow-x-auto">
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end
          className={({ isActive }) =>
            `relative px-4 py-2.5 text-[13px] whitespace-nowrap transition-colors ${
              isActive
                ? "text-foreground font-medium"
                : "text-muted-foreground hover:text-foreground"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <span className="inline-flex items-center gap-1.5">
                {t.label}
                {t.count != null && t.count > 0 && (
                  <span
                    className={`inline-flex items-center justify-center min-w-[18px] px-1 h-[18px] text-[10px] tabular-nums font-medium ${
                      t.tone === "bad"
                        ? "bg-destructive/15 text-destructive"
                        : t.tone === "warn"
                        ? "bg-warning/15 text-warning"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {t.count}
                  </span>
                )}
              </span>
              {isActive && (
                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary" />
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * FinanceNavStrip — the canonical Finance sub-nav.
 * Pulls counts from useFinanceCounts so every page renders the same
 * strip and badges stay current as data changes.
 */
export function FinanceNavStrip() {
  const counts = useFinanceCounts();
  return (
    <FinanceTabs
      tabs={[
        { to: "/accounting", label: "Overview" },
        { to: "/finance/approvals", label: "Approvals", count: counts.approvalsCount, tone: counts.approvalsCritical > 0 ? "bad" : "warn" },
        { to: "/finance/collections", label: "Collections", count: counts.collectionsOverdue, tone: "warn" },
        { to: "/finance/purchase-orders", label: "POs", count: counts.poAttention, tone: counts.poAttention > 0 ? "warn" : "muted" },
        { to: "/finance/bills", label: "Bills", count: counts.billsOverdue, tone: counts.billsOverdue > 0 ? "bad" : "muted" },
        { to: "/finance/payroll", label: "Payroll", count: counts.payrollRecords, tone: "muted" },
        { to: "/finance/close", label: "Period Close", count: counts.closeOpenCount, tone: "muted" },
        { to: "/finance/pay-runs", label: "Pay Runs" },
        { to: "/finance/payouts", label: "Payouts" },
      ]}
    />
  );
}
